import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import EmptyState from '../../components/attendee/EmptyState';
import LoadingState from '../../components/attendee/LoadingState';
import { formatDate } from '../../utils/attendeeUtils';

const AttendeeNotificationsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (user) fetchNotifications();
  }, [user]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const notifs = [];

      // Get upcoming registered events as reminders
      const { data: registrations, error } = await supabase
        .from('event_registrations')
        .select('event_id, status, events(id, title, start_date, end_date, location, venue_name)')
        .eq('attendee_email', user.email)
        .neq('status', 'cancelled');

      if (!error && registrations) {
        const today = new Date();
        registrations.forEach(reg => {
          const event = reg.events;
          if (event) {
            const eventDate = new Date(event.start_date);
            const daysUntil = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));

            if (daysUntil >= 0 && daysUntil <= 30) {
              notifs.push({
                id: `event-${event.id}`,
                type: 'event_reminder',
                category: 'Event Reminders',
                title: 'Upcoming Event',
                message: `${event.title} is in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`,
                date: event.start_date,
                event: event,
                actionText: 'View Event',
                actionUrl: `/my-events/${event.id}`,
              });
            }
          }
        });
      }

      // Sort by date
      notifs.sort((a, b) => new Date(a.date) - new Date(b.date));
      setNotifications(notifs);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'event_reminder':
        return (
          <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="flex-shrink-0 w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  // Group notifications by category
  const grouped = notifications.reduce((acc, notif) => {
    const category = notif.category || 'General';
    if (!acc[category]) acc[category] = [];
    acc[category].push(notif);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48 animate-pulse mb-6" />
        <LoadingState count={3} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          Notifications
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Stay updated on your events and activities.
        </p>
      </div>

      {/* Notifications by category */}
      {notifications.length > 0 ? (
        <div className="space-y-6">
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category}>
              <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                {category}
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                {items.map(notif => (
                  <div key={notif.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <div className="flex items-start">
                      {getNotificationIcon(notif.type)}
                      <div className="ml-3 flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{notif.title}</p>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{notif.message}</p>
                        <div className="mt-2 flex items-center justify-between">
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {formatDate(notif.date)}
                          </p>
                          {notif.actionUrl && (
                            <Link
                              to={notif.actionUrl}
                              className="text-xs text-primary hover:text-primary-dark font-medium"
                            >
                              {notif.actionText} →
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          title="No notifications"
          message="You're all caught up! Event reminders will appear here as your events approach."
          ctaText="Browse Events"
          ctaLink="/events"
        />
      )}

      {/* Notification preferences link */}
      <div className="mt-8">
        <Link
          to="/attendee/profile"
          className="block p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Manage notification preferences</span>
            </div>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default AttendeeNotificationsPage;
